import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Share2, Copy, ExternalLink, Loader2 } from 'lucide-react';
import { API } from '../utils/constants';

/**
 * Botón "Compartir con cliente" reutilizable para cualquier módulo de redacción.
 *
 * Genera un link público (slug + token) llamando a `POST {API}{endpoint}`, lo
 * convierte a URL absoluta y lo muestra en un modal con copiar/abrir. El backend
 * NO reusa publicaciones: cada llamada crea un link nuevo, así que mostramos el
 * último generado si el modal se reabre.
 *
 * Props:
 *   endpoint  — ruta del backend, p. ej. `/whitepapers/${id}/publish`
 *   prefill   — { client_name, client_email, version } opcionales
 *   label     — texto del botón
 *   className — clases extra para el botón
 */
export function PublishForClientButton({
  endpoint,
  prefill = {},
  label = '🔗 Compartir con cliente',
  className = '',
}) {
  const [publishing, setPublishing] = useState(false);
  const [publishedUrl, setPublishedUrl] = useState('');
  const [showModal, setShowModal] = useState(false);

  const publish = async () => {
    if (publishedUrl) {
      setShowModal(true);
      return;
    }
    setPublishing(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}${endpoint}`,
        {
          client_name: prefill.client_name || '',
          client_email: prefill.client_email || '',
          version: prefill.version || 1,
        },
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
      );
      // El backend devuelve URL relativa (`/p/{slug}?token=...`).
      setPublishedUrl(`${window.location.origin}${data.url}`);
      setShowModal(true);
      toast.success('🔗 Link de cliente generado');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Error al generar el link');
    } finally {
      setPublishing(false);
    }
  };

  return (
    <>
      <Button
        onClick={publish}
        variant="outline"
        disabled={publishing}
        className={`bg-amber-50 border-amber-300 hover:bg-amber-100 ${className}`}
        data-testid="publish-for-client-btn"
        title="Genera un link público para que el cliente revise este borrador"
      >
        {publishing
          ? <Loader2 className="mr-2 animate-spin" size={18} />
          : <Share2 className="mr-2" size={18} />}
        {label}
      </Button>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 size={20} className="text-amber-600" />
              Link para revisión del cliente
            </DialogTitle>
            <DialogDescription>
              Copia este link y envíalo al cliente. Verá el documento en formato de
              revisión (sin login): puede leer, comentar o solicitar cambios, y aprobar.
              Las correcciones se aplican automáticamente ~1 hora después de cada
              comentario, en hasta 2 rondas.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="flex items-stretch gap-2">
              <Input
                value={publishedUrl}
                readOnly
                className="font-mono text-xs"
                data-testid="publish-url-input"
                onFocus={(e) => e.target.select()}
              />
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(publishedUrl);
                  toast.success('Link copiado al portapapeles');
                }}
                variant="outline"
                data-testid="copy-publish-url-btn"
              >
                <Copy className="mr-1" size={16} />
                Copiar
              </Button>
              <Button
                onClick={() => window.open(publishedUrl, '_blank', 'noopener,noreferrer')}
                variant="outline"
                data-testid="open-publish-url-btn"
              >
                <ExternalLink className="mr-1" size={16} />
                Abrir
              </Button>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
              <p className="font-semibold mb-1">⚠️ Importante</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>El token va en la URL — quien tenga el link ve el documento. No lo publiques en canales abiertos.</li>
                <li>Cada vez que regeneres el link se crea uno nuevo; los anteriores siguen activos.</li>
                <li>Los comentarios del cliente se aplican en hasta 2 rondas (ventana de ~1 hora cada una).</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default PublishForClientButton;
